/**
 * German, Goethe-Zertifikat A1 to C2 (D-118): the pure logic shared by the web and the mobile app.
 *
 * - The levels and the learner's profile (`GET academy/goethe/`), the format of a level
 *   (`GET academy/format/?exam=goethe&level=B1`), a sitting sent by the server (Hören one
 *   document at a time with its part, plays and timings; Lesen by part; Schreiben and Sprechen by
 *   part) and the results on the official scale of the level, always an estimate.
 * - The scales, as the server computes them (A1 points x 1.66, A2 x 1.25, B1 to C1 the official
 *   table of 30 items, C2 weighted parts), the pass marks (A1 / A2 on the total, B1 to C2 per
 *   module at 60) and the ratings (« sehr gut » … « nicht bestanden »). Every number comes from
 *   the versioned format table: nothing about the exam is written here. No React, no platform.
 */

import { isObject, num, numOrNull, objects, str, strings } from "../ielts/read";
import { type SpokenLine, parseSpokenLines } from "../listening";
import { type ExamPicture, parsePicture, parsePictures } from "../pictures";
import { countWords } from "../tcf";

export const GOETHE_EXAM = "goethe";
export const GOETHE_LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"] as const;
export type GoetheLevel = (typeof GOETHE_LEVELS)[number];
export type GoetheSkill = "co" | "ce" | "ee" | "eo";
export const GOETHE_SKILLS: readonly GoetheSkill[] = ["co", "ce", "ee", "eo"];
/** The German name of each module (shown as it is on the certificate). */
export const GOETHE_MODULES: Record<GoetheSkill, string> = { co: "Hören", ce: "Lesen", ee: "Schreiben", eo: "Sprechen" };

export function isGoetheLevel(value: unknown): value is GoetheLevel {
  return typeof value === "string" && (GOETHE_LEVELS as readonly string[]).includes(value.toUpperCase());
}

export function goetheLevel(value: unknown): GoetheLevel | "" {
  return isGoetheLevel(value) ? (value.toUpperCase() as GoetheLevel) : "";
}

function isSkill(value: unknown): value is GoetheSkill {
  return value === "co" || value === "ce" || value === "ee" || value === "eo";
}

type Localized = { fr: string; en: string; de: string };

function localized(value: unknown): Localized {
  const v = isObject(value) ? value : {};
  return { fr: str(v.fr), en: str(v.en), de: str(v.de) };
}

/** A localized label in the interface language (French, else English, else German). */
export function pickLabel(value: Localized | undefined, language: string): string {
  if (!value) return "";
  return (language.startsWith("en") ? value.en || value.fr : value.fr || value.en) || value.de;
}

// --- Ratings and pass marks --------------------------------------------------------------------

export interface GoetheRating {
  de: string;
  fr: string;
  en: string;
  min: number;
}

export interface GoetheRatingRow extends GoetheRating {
  max: number;
}

/** Rounded half up, like the official rules (0.5 goes up); `places` decimals. */
export function halfUp(value: number, places = 0): number {
  const factor = 10 ** places;
  return Math.round(value * factor + 1e-9) / factor;
}

export function parseRatings(value: unknown): GoetheRatingRow[] {
  return objects(value).map((r) => ({ de: str(r.de), fr: str(r.fr), en: str(r.en), min: num(r.min), max: num(r.max, 100) }));
}

/** The « Prädikat » of a result out of 100, as the server gives it (rounded half up first). */
export function ratingFor(points: number, ratings: GoetheRatingRow[]): GoetheRating | null {
  const value = halfUp(points);
  const row = ratings.find((r) => value >= r.min && value <= r.max);
  return row ? { de: row.de, fr: row.fr, en: row.en, min: row.min } : null;
}

/** Points written as on a result sheet: « 19,9 » in French, « 19.9 » in English (1 decimal at most). */
export function formatPoints(value: number | null, language: string): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(language.startsWith("en") ? "en-GB" : "fr-FR", { maximumFractionDigits: 1 }).format(halfUp(value, 1));
}

// --- Scales (as the server computes them) -----------------------------------------------------

export interface GoetheScaleData {
  /** [right answers out of 30, points out of 100], the official table of B1, B2 and C1. */
  table30: [number, number][];
  a1Factor: number;
  a2Factor: number;
  /** C2: weight of each part, per module. */
  c2Weights: Record<"reading" | "listening" | "writing", Record<string, number>>;
  c2SpeakingFactor: number;
}

export function parseScale(details: unknown): GoetheScaleData {
  const d = isObject(details) ? details : {};
  const conversion = isObject(d.conversion) ? d.conversion : {};
  const table = isObject(conversion.table_30) ? conversion.table_30 : {};
  const a1 = isObject(conversion.a1_factor) ? conversion.a1_factor : {};
  const a2 = isObject(conversion.a2_factor) ? conversion.a2_factor : {};
  const c2 = isObject(conversion.c2_weights) ? conversion.c2_weights : {};
  const weights = (key: string): Record<string, number> => {
    const raw = isObject(c2[key]) ? (c2[key] as Record<string, unknown>) : {};
    return Object.fromEntries(Object.entries(raw).map(([k, v]) => [k, num(v, 1)]));
  };
  return {
    table30: (Array.isArray(table.rows) ? table.rows : [])
      .filter((row): row is unknown[] => Array.isArray(row) && row.length === 2)
      .map((row) => [num(row[0]), num(row[1])] as [number, number]),
    a1Factor: num(a1.factor, 1.66),
    a2Factor: num(a2.factor, 1.25),
    c2Weights: { reading: weights("reading"), listening: weights("listening"), writing: weights("writing") },
    c2SpeakingFactor: num(c2.speaking_factor, 2.5),
  };
}

/** Right answers out of 30 → points out of 100 (official table). */
export function table30(right: number, scale: GoetheScaleData): number {
  const value = halfUp(right);
  for (const [raw, points] of [...scale.table30].sort((a, b) => b[0] - a[0])) if (value >= raw) return points;
  return 0;
}

/**
 * Lesen or Hören: right answers by part → points of the module (A1 x 1.66 out of 24.9, A2 x 1.25
 * out of 25, B1–C1 official table out of 100, C2 weighted parts out of 100). `items` is the
 * number of items of the module (a shorter practice is scaled to it first).
 */
export function receptivePoints(
  level: GoetheLevel,
  skill: "co" | "ce",
  byTeil: Record<string, { right: number; items: number }>,
  items: number,
  scale: GoetheScaleData,
): { points: number; max: number } {
  const rows = Object.entries(byTeil);
  const right = rows.reduce((sum, [, r]) => sum + r.right, 0);
  const asked = rows.reduce((sum, [, r]) => sum + r.items, 0);
  const full = items || asked || 1;
  const scaled = asked ? (right * full) / asked : 0;
  if (level === "A1") return { points: halfUp(scaled * scale.a1Factor, 2), max: halfUp(full * scale.a1Factor, 2) };
  if (level === "A2") return { points: halfUp(scaled * scale.a2Factor, 2), max: halfUp(full * scale.a2Factor, 2) };
  if (level === "C2") {
    const weights = scale.c2Weights[skill === "co" ? "listening" : "reading"];
    const earned = rows.reduce((sum, [teil, r]) => sum + r.right * (weights[teil] ?? 1), 0);
    const possible = rows.reduce((sum, [teil, r]) => sum + r.items * (weights[teil] ?? 1), 0);
    return { points: possible ? halfUp((earned * 100) / possible, 2) : 0, max: 100 };
  }
  return { points: table30((scaled * 30) / full, scale), max: 100 };
}

/** Pass of a module (B1 to C2: 60 % of the module, rounded half up). */
export function modulePassed(points: number, max: number, passMark = 60): boolean {
  return max > 0 && halfUp((points * 100) / max) >= passMark;
}

/** A1 and A2: the total and its conditions (A2: 45 at the written part and 15 at the oral). */
export function nonModularPassed(
  level: "A1" | "A2",
  written: number,
  oral: number,
  scoring: { passTotal?: number; passWritten?: number; passOral?: number } = {},
): { total: number; passed: boolean } {
  const total = halfUp(written + oral);
  let passed = total >= (scoring.passTotal ?? 60);
  if (level === "A2") passed = passed && written >= (scoring.passWritten ?? 45) && oral >= (scoring.passOral ?? 15);
  return { total, passed };
}

// --- Profile and levels (GET academy/goethe/) -------------------------------------------------

export interface GoetheLevelProfile {
  level: GoetheLevel;
  examDate: string | null;
  examCenter: string;
  modules: GoetheSkill[];
  target: number;
  xp: number;
  levelIndex: number | null;
}

export interface GoetheCatalogLevel {
  level: GoetheLevel;
  officialName: string;
  modular: boolean;
  writtenMinutes: number;
  order: GoetheSkill[];
  setting: string;
  modules: Partial<Record<GoetheSkill, { minutes: number; pointsMax: number; parts: number; label: Localized }>>;
}

export interface GoetheProject {
  key: string;
  level: GoetheLevel | "";
  label: Localized;
  hint: Localized;
}

export interface GoetheState {
  hasAccess: boolean;
  activeLevel: GoetheLevel | "";
  recommendedLevel: GoetheLevel | "";
  project: string;
  projectLevel: GoetheLevel | "";
  courseLevel: string;
  beginner: boolean;
  placementLevel: GoetheLevel | "";
  levels: GoetheLevelProfile[];
  catalog: GoetheCatalogLevel[];
  projects: GoetheProject[];
  projectSource: Localized;
  mocks: Partial<Record<GoetheLevel, number>>;
  centers: { code: string; name: string; city: string; country: string }[];
}

export function parseGoetheState(value: unknown): GoetheState {
  const data = isObject(value) ? value : {};
  const placement = isObject(data.placement) ? data.placement : {};
  const projects = isObject(data.projects) ? data.projects : {};
  const mocks = isObject(data.mocks) ? data.mocks : {};
  return {
    hasAccess: data.has_access === true,
    activeLevel: goetheLevel(data.active_level),
    recommendedLevel: goetheLevel(data.recommended_level),
    project: str(data.project),
    projectLevel: goetheLevel(data.project_level),
    courseLevel: str(data.course_level),
    beginner: data.beginner === true,
    placementLevel: goetheLevel(placement.cefr),
    levels: objects(data.levels)
      .filter((row) => isGoetheLevel(row.level))
      .map((row) => ({
        level: goetheLevel(row.level) as GoetheLevel,
        examDate: str(row.exam_date) || null,
        examCenter: str(row.exam_center),
        modules: strings(row.modules).filter(isSkill),
        target: num(row.target, 60),
        xp: num(row.xp),
        levelIndex: numOrNull(row.level_index),
      })),
    catalog: objects(data.catalog)
      .filter((row) => isGoetheLevel(row.level))
      .map((row) => {
        const modules = isObject(row.modules) ? row.modules : {};
        const out: GoetheCatalogLevel["modules"] = {};
        for (const skill of GOETHE_SKILLS) {
          const m = isObject(modules[skill]) ? (modules[skill] as Record<string, unknown>) : null;
          if (m) out[skill] = { minutes: num(m.minutes), pointsMax: num(m.points_max), parts: num(m.parts), label: localized(m.label) };
        }
        return {
          level: goetheLevel(row.level) as GoetheLevel,
          officialName: str(row.official_name),
          modular: row.modular === true,
          writtenMinutes: num(row.written_minutes),
          order: strings(row.order).filter(isSkill),
          setting: str(row.setting),
          modules: out,
        };
      }),
    projects: objects(projects.projects).map((p) => ({ key: str(p.key), level: goetheLevel(p.level), label: localized(p.label), hint: localized(p.hint) })),
    projectSource: localized(projects.source_hint),
    mocks: Object.fromEntries(GOETHE_LEVELS.map((level) => [level, num(mocks[level])])),
    centers: objects(data.centers).map((c) => ({ code: str(c.code), name: str(c.name), city: str(c.city), country: str(c.country) })),
  };
}

/** What the German home screen suggests next (identical on the web and the phone). */
export type GoetheStep = "choose_level" | "placement" | "plans" | "course" | "mock";

export function goetheNextStep(state: GoetheState, opts: { courseNext?: string | null; mocksDone?: number } = {}): GoetheStep {
  if (!state.activeLevel) return state.placementLevel || state.recommendedLevel ? "choose_level" : "placement";
  if (!state.hasAccess) return "plans";
  if (opts.courseNext && (opts.mocksDone ?? 0) === 0) return "course";
  return "mock";
}

// --- Results ----------------------------------------------------------------------------------

export interface GoetheModuleResult {
  skill: GoetheSkill;
  pending: boolean;
  points: number | null;
  max: number | null;
  percent: number | null;
  passed: boolean | null;
  rating: GoetheRating | null;
  right: number | null;
  items: number | null;
  teile: Record<string, { right?: number; items?: number; points?: number }>;
  thirdRating: boolean;
  eRule: boolean;
}

export interface GoetheResults {
  level: GoetheLevel | "";
  modules: Partial<Record<GoetheSkill, GoetheModuleResult>>;
  overall:
    | { kind: "total"; total: number; max: number; written: number; oral: number; writtenMax: number; oralMax: number; passed: boolean; rating: GoetheRating | null; writtenTooLow: boolean }
    | { kind: "modules"; modules: GoetheSkill[]; passedModules: GoetheSkill[]; allPassed: boolean }
    | null;
}

function rating(value: unknown): GoetheRating | null {
  return isObject(value) && str(value.de) ? { de: str(value.de), fr: str(value.fr), en: str(value.en), min: num(value.min) } : null;
}

export function parseGoetheResults(value: unknown): GoetheResults {
  const data = isObject(value) ? value : {};
  const modules: GoetheResults["modules"] = {};
  for (const skill of GOETHE_SKILLS) {
    const raw = data[skill];
    if (!isObject(raw)) continue;
    const teile: GoetheModuleResult["teile"] = {};
    if (isObject(raw.teile)) {
      for (const [teil, row] of Object.entries(raw.teile)) {
        teile[teil] = isObject(row) ? { right: num(row.right), items: num(row.items) } : { points: num(row) };
      }
    }
    modules[skill] = {
      skill,
      pending: raw.pending === true,
      points: numOrNull(raw.points),
      max: numOrNull(raw.max),
      percent: numOrNull(raw.percent),
      passed: typeof raw.passed === "boolean" ? raw.passed : null,
      rating: rating(raw.rating),
      right: numOrNull(raw.right),
      items: numOrNull(raw.items),
      teile,
      thirdRating: raw.third_rating === true,
      eRule: raw.e_rule === true,
    };
  }
  const o = isObject(data.overall) ? data.overall : null;
  let overall: GoetheResults["overall"] = null;
  if (o && typeof o.total === "number") {
    overall = {
      kind: "total",
      total: num(o.total),
      max: num(o.max, 100),
      written: num(o.written),
      oral: num(o.oral),
      writtenMax: num(o.written_max, 75),
      oralMax: num(o.oral_max, 25),
      passed: o.passed === true,
      rating: rating(o.rating),
      writtenTooLow: o.written_too_low === true,
    };
  } else if (o) {
    overall = {
      kind: "modules",
      modules: strings(o.modules).filter(isSkill),
      passedModules: strings(o.passed_modules).filter(isSkill),
      allPassed: o.all_passed === true,
    };
  }
  return { level: goetheLevel(data.level), modules, overall };
}

// --- A sitting (the player's state) ------------------------------------------------------------

export interface GoethePoolItem {
  key: string;
  text: string;
  /** A2 Hören Teil 2: the picture to match (the text is then its description), or null. */
  image: ExamPicture | null;
}

export interface GoetheExample {
  prompt: string;
  options: string[];
  answer: number | null;
  answerKey: string;
  answerText: string;
  explanationFr: string;
  explanationEn: string;
  hasScript: boolean;
}

export interface GoetheText {
  id: string;
  type: string;
  title: string;
  author: string;
  text: string;
}

export interface GoetheQuestion {
  id: string;
  index: number;
  number: number;
  stem: string;
  options: string[];
  format: string;
  teil: number;
  textId: string;
  textIds: string[];
  response: number | null;
  image: ExamPicture | null;
  /** A2 Hören Teil 3: one picture per option (« Bild a: Kaffee »), null where none. */
  optionImages: (ExamPicture | null)[];
}

export interface GoethePassage {
  id: string;
  teil: number;
  title: string;
  context: string;
  instructions: string;
  example: GoetheExample | null;
  pool: GoethePoolItem[];
  texts: GoetheText[];
  hasAudio: boolean;
  type: string;
  plays: number;
  doc: number;
  docs: number;
  first: boolean;
  lines: SpokenLine[];
  exampleLines: SpokenLine[];
  image: ExamPicture | null;
  /** The pictures of the example's options, null where none. */
  exampleOptionImages: (ExamPicture | null)[];
}

export interface GoetheTask {
  id: string;
  teil: number;
  skill: "ee" | "eo";
  prompt: string;
  pointsMax: number;
  limits: Record<string, unknown>;
  material: Record<string, unknown>;
  text: string;
  responses: Record<string, unknown>;
  wordCount: number;
  status: string;
  examiner: { live: boolean; setting: string; turns: { role: string; text: string }[] };
}

export interface GoetheSitting {
  status: string;
  level: GoetheLevel | "";
  officialName: string;
  kind: string;
  mode: string;
  section: GoetheSkill | "";
  remaining: number;
  skills: GoetheSkill[];
  answered: number;
  total: number;
  part: number;
  parts: number;
  flow: { readingSeconds: number; answerSeconds: number; plays: number; betweenPlaysMs: number; readingEach: boolean };
  current: { passage: GoethePassage | null; questions: GoetheQuestion[] } | null;
  sections: { teil: number; groups: { passage: GoethePassage | null; questions: GoetheQuestion[] }[] }[];
  navigable: boolean;
  fixedOrder: boolean;
  /** D-163: A1 Lesen and Schreiben, one block of 45 minutes in the order the learner chooses
   * (the modules of the block, e.g. ["ce", "ee"]), or null. */
  block: { skills: GoetheSkill[]; minutes: number } | null;
  tasks: GoetheTask[];
  prep: { minutes: number; seconds: number; notesAllowed: boolean };
  setting: string;
  results: GoetheResults | null;
}

function example(value: unknown): GoetheExample | null {
  if (!isObject(value)) return null;
  return {
    prompt: str(value.prompt),
    options: strings(value.options),
    answer: numOrNull(value.answer),
    answerKey: str(value.answer_key),
    answerText: str(value.answer_text),
    explanationFr: str(value.explanation_fr),
    explanationEn: str(value.explanation_en),
    hasScript: value.has_script === true,
  };
}

export function parseGoethePassage(value: unknown): GoethePassage | null {
  if (!isObject(value)) return null;
  return {
    id: str(value.id),
    teil: num(value.teil, num(value.number)),
    title: str(value.title),
    context: str(value.context),
    instructions: str(value.instructions),
    example: example(value.example),
    pool: objects(value.pool).map((p) => ({ key: str(p.key), text: str(p.text), image: parsePicture(p.image) })),
    texts: objects(value.texts).map((t) => ({ id: str(t.id), type: str(t.type), title: str(t.title), author: str(t.author), text: str(t.text) })),
    hasAudio: value.has_audio === true,
    type: str(value.type),
    plays: num(value.plays, 1),
    doc: num(value.doc, 1),
    docs: num(value.docs, 1),
    first: value.first === true,
    lines: parseSpokenLines(value.lines),
    exampleLines: parseSpokenLines(value.example_lines),
    image: parsePicture(value.image),
    exampleOptionImages: parsePictures(value.example_option_images),
  };
}

function question(value: Record<string, unknown>): GoetheQuestion {
  return {
    id: str(value.id),
    index: num(value.index),
    number: num(value.number),
    stem: str(value.stem),
    options: strings(value.options),
    format: str(value.format),
    teil: num(value.teil),
    textId: str(value.text_id),
    textIds: strings(value.text_ids),
    response: numOrNull(value.response),
    image: parsePicture(value.image),
    optionImages: parsePictures(value.option_images),
  };
}

function task(value: Record<string, unknown>): GoetheTask {
  const examiner = isObject(value.examiner) ? value.examiner : {};
  return {
    id: str(value.id),
    teil: num(value.teil, num(value.number)),
    skill: value.skill === "eo" ? "eo" : "ee",
    prompt: str(value.prompt),
    pointsMax: num(value.points_max),
    limits: isObject(value.limits) ? value.limits : {},
    material: isObject(value.material) ? value.material : {},
    text: str(value.text),
    responses: isObject(value.responses) ? value.responses : {},
    wordCount: num(value.word_count),
    status: str(value.status),
    examiner: {
      live: examiner.live === true,
      setting: str(examiner.setting),
      turns: objects(examiner.turns).map((t) => ({ role: str(t.role), text: str(t.text) })),
    },
  };
}

export function parseGoetheSitting(value: unknown): GoetheSitting {
  const data = isObject(value) ? value : {};
  const flow = isObject(data.flow) ? data.flow : {};
  const current = isObject(data.current) ? data.current : null;
  const prep = isObject(data.prep) ? data.prep : {};
  return {
    status: str(data.status),
    level: goetheLevel(data.level),
    officialName: str(data.official_name),
    kind: str(data.kind),
    mode: str(data.mode),
    section: isSkill(data.section) ? data.section : "",
    remaining: num(data.remaining_seconds),
    skills: strings(data.skills).filter(isSkill),
    answered: num(data.answered),
    total: num(data.total),
    part: num(data.part, 1),
    parts: num(data.parts, 1),
    flow: {
      readingSeconds: num(flow.reading_seconds, 10),
      answerSeconds: num(flow.answer_seconds, 10),
      plays: num(flow.plays, 1),
      betweenPlaysMs: num(flow.between_plays_ms, 3000),
      readingEach: flow.reading_each === true,
    },
    current: current ? { passage: parseGoethePassage(current.passage), questions: objects(current.questions).map(question) } : null,
    sections: objects(data.sections).map((s) => ({
      teil: num(s.teil),
      groups: objects(s.groups).map((g) => ({ passage: parseGoethePassage(g.passage), questions: objects(g.questions).map(question) })),
    })),
    navigable: data.navigable === true,
    fixedOrder: data.fixed_order === true,
    block: isObject(data.block) ? { skills: strings(data.block.skills).filter(isSkill), minutes: num(data.block.minutes) } : null,
    tasks: objects(data.tasks).map(task),
    prep: { minutes: num(prep.minutes), seconds: num(prep.seconds), notesAllowed: prep.notes_allowed === true },
    setting: str(data.setting),
    results: isObject(data.results) ? parseGoetheResults(data.results) : null,
  };
}

/** The texts a question refers to (one text, or the two ads of an A1 Lesen part 2 item). */
export function textsOf(question: GoetheQuestion, passage: GoethePassage | null): GoetheText[] {
  if (!passage) return [];
  const ids = question.textIds.length ? question.textIds : question.textId ? [question.textId] : [];
  return ids.map((id) => passage.texts.find((t) => t.id === id)).filter((t): t is GoetheText => Boolean(t));
}

/** The option of a match item shown with its pool text (« a: … »); « 0 » is the official mark
 * for « no solution » (B1 Lesen Teil 3, A2 Lesen Teil 4, D-163), said in German with it. */
export const NONE_LABEL = "0: keine Lösung";
export function optionLabel(option: string, pool: GoethePoolItem[]): string {
  if (option === "0") return NONE_LABEL;
  const item = pool.find((p) => p.key === option);
  return item ? `${option}: ${item.text}` : option;
}

/** Writing: the words counted as the server counts them, and the band asked for the part. */
export function wordsStatus(text: string, limits: Record<string, unknown>): { count: number; min: number; max: number; tooShort: boolean; halfWarning: boolean } {
  const count = countWords(text);
  const words = num(limits.words);
  const min = num(limits.words_min, words ? Math.round(words * 0.7) : 0);
  const max = num(limits.words_max, words ? Math.round(words * 1.5) : 0);
  const asked = num(limits.words_min, words);
  return { count, min, max, tooShort: min > 0 && count < min, halfWarning: asked > 0 && count < asked * 0.5 };
}

/** Speaking: the speaker's role of a reply (« examiner » or « partner »), for the voice. */
export function speakerOf(role: unknown): "examiner" | "partner" {
  return role === "partner" ? "partner" : "examiner";
}

/** The number of listenings of a Hören document, as a word for the player (1 → « once »). */
/** Seconds to read the items before a text is heard (D-137): the full reading time before the
 *  first text of a part, and before each text when the part says so (C1 Teil 3: 30 s before each
 *  extract); a short pause between the other texts of a part. */
export function readingSecondsFor(passage: GoethePassage | null, flow: { readingSeconds: number; readingEach: boolean }, between = 5): number {
  if (!passage) return 0;
  return passage.first || flow.readingEach ? flow.readingSeconds : Math.min(flow.readingSeconds, between);
}

export function playsCount(passage: GoethePassage | null): number {
  return Math.max(1, passage?.plays ?? 1);
}

/** The level of a numbered list of right answers per part, as shown in a review. */
export function rightByTeil(
  questions: { skill: string; teil: number; correct: boolean }[],
  skill: GoetheSkill,
): Record<string, { right: number; items: number }> {
  const out: Record<string, { right: number; items: number }> = {};
  for (const q of questions) {
    if (q.skill !== skill) continue;
    const row = (out[String(q.teil)] ??= { right: 0, items: 0 });
    row.items += 1;
    row.right += q.correct ? 1 : 0;
  }
  return out;
}


// --- Progress of one level (D-123: shared by the learner page, the back office and the phone) ---

export interface GoetheModuleProgress {
  points: number | null;
  max: number | null;
  percent: number | null;
  gap: number | null;
  /** The rating in German (« gut »…), empty when the module was not taken. */
  rating: string;
}

export interface GoetheProgress {
  verdict: string;
  target: number | null;
  rows: Record<GoetheSkill, GoetheModuleProgress>;
  weeks: number | null;
  critical: string[];
  evolution: { at: string; kind: string; mock: number | null; modules: Record<string, unknown> }[];
  rank: { position: number; total: number; top: number } | null;
}

/** `GET academy/progress/?exam=goethe&level=…` (and the trainer's learner progress): the
 * verdict, the points of each module on the official scale, the plan, the evolution, the rank. */
export function parseGoetheProgress(value: unknown): GoetheProgress {
  const data = isObject(value) ? value : {};
  const analysis = isObject(data.analysis) ? data.analysis : {};
  const modules = isObject(analysis.modules) ? analysis.modules : {};
  const rows = Object.fromEntries(
    GOETHE_SKILLS.map((skill) => {
      const row = isObject(modules[skill]) ? modules[skill] : {};
      const rating = isObject(row.rating) ? row.rating : null;
      return [skill, { points: numOrNull(row.points), max: numOrNull(row.max), percent: numOrNull(row.percent), gap: numOrNull(row.gap), rating: rating ? str(rating.de) : "" }];
    }),
  ) as Record<GoetheSkill, GoetheModuleProgress>;
  const plan = isObject(analysis.plan) ? analysis.plan : {};
  const rank = isObject(data.rank) && isObject(data.rank.platform) ? data.rank.platform : null;
  return {
    verdict: typeof analysis.verdict === "string" ? analysis.verdict : "not_ready",
    target: numOrNull(analysis.target),
    rows,
    weeks: numOrNull(plan.weeks),
    critical: objects(analysis.critical).map((c) => str(c.message)).filter(Boolean),
    evolution: objects(data.evolution).map((row) => ({
      at: str(row.at),
      kind: str(row.kind),
      mock: numOrNull(row.mock),
      modules: isObject(row.modules) ? row.modules : {},
    })),
    rank: rank ? { position: numOrNull(rank.position) ?? 0, total: numOrNull(rank.total) ?? 0, top: numOrNull(rank.top_percent) ?? 100 } : null,
  };
}
